import * as React from 'react';
import {
  AccountsError,
  discoverSso,
  resendSignup,
  SignInError,
  signInOptions,
  signupOptions,
  ssoErrorText,
  startSso,
  type SignInOptions,
  type SsoConnection,
} from '../accounts.ts';
import { Link, useLocation, useNavigate } from 'react-router';
import { config } from '../../platform/index.ts';
import { useAuth } from '../auth.tsx';
import type { MfaState } from './MfaVerification.tsx';
import { ProviderIcon } from './sso-icons.tsx';

const INPUT =
  'h-9 px-3 rounded-[5px] border text-sm outline-none transition-colors disabled:opacity-50 bg-card-bg border-[var(--input-border)] text-page-text focus:border-nav-active';

const ICON_BTN =
  'h-10 w-12 inline-flex items-center justify-center rounded-[5px] cursor-pointer border disabled:opacity-50 disabled:cursor-not-allowed bg-card-bg border-[var(--input-border)] hover:border-nav-active focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nav-active transition-colors';

/** The longest app name the sign-in page shows. */
export const APP_NAME_MAX = 80;

/** The app that asked for this sign-in (`?app=`), as plain text of at most APP_NAME_MAX characters, or ''. */
export function appNameOf(search: string) {
  return (new URLSearchParams(search).get('app') ?? '').trim().slice(0, APP_NAME_MAX);
}

/** This page's URL without a previous SSO error: where a single sign-on comes back to. */
function comeBackUrl() {
  const u = new URL(window.location.href);
  u.searchParams.delete('sso_error');
  return u.toString();
}

/**
 * The sign-in page: one email and password form, and under it one icon button per single sign-on
 * connection of this installation (ADR 0014), which starts that provider's sign-in directly. With no
 * connection the row and its divider are not there. The password form is there unless the installation
 * turned passwords off; the accounts service decides whose password it takes (ACCOUNTS_PASSWORD_LOGIN)
 * and answers one way for an unknown email and a wrong password. An email whose organization signs in
 * with a connection hidden from this page (show on sign-in off) adds that connection's button.
 */
export function LoginPage() {
  const { state, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const routed = location.state as { from?: MfaState['from']; notice?: string } | null;
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState(() => {
    const reason = new URLSearchParams(window.location.search).get('sso_error');
    // A two-factor step that ran out (expired, too many wrong codes) sends the person back here to say so.
    return reason ? ssoErrorText(reason) : (routed?.notice ?? '');
  });
  const [busy, setBusy] = React.useState(false);
  const [unverified, setUnverified] = React.useState(false);
  const [resent, setResent] = React.useState(false);
  const [canSignUp, setCanSignUp] = React.useState(false);
  const appName = appNameOf(location.search);
  const [options, setOptions] = React.useState<SignInOptions>({ connections: [], password: 'on' });
  const [discovered, setDiscovered] = React.useState<SsoConnection | null>(null);
  const apiError = state.status === 'signed-out' ? state.error : undefined;

  React.useEffect(() => {
    let cancelled = false;
    void signInOptions().then((o) => !cancelled && setOptions(o));
    void signupOptions().then((o) => !cancelled && setCanSignUp(o.enabled));
    return () => {
      cancelled = true;
    };
  }, []);

  const onEmailBlur = async () => {
    const e = email.trim();
    setDiscovered(e.includes('@') ? await discoverSso(e) : null);
  };
  const showPassword = options.password !== 'off';
  const buttons =
    discovered && !options.connections.some((c) => c.id === discovered.id)
      ? [...options.connections, discovered]
      : options.connections;
  const shownError = error || apiError;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    setUnverified(false);
    setResent(false);
    try {
      const challenge = await login(email.trim(), password);
      // The password was right; the second factor is next (ADR 0014 §9). The deep link and any
      // preview `?return=` travel along.
      if (challenge) {
        const next: MfaState = {
          mfa: challenge,
          email: email.trim(),
          ...(routed?.from ? { from: routed.from } : {}),
        };
        navigate(`/mfa-verification${location.search}`, { state: next });
        return;
      }
    } catch (err) {
      setError(err instanceof SignInError ? err.message : 'Sign-in failed.');
      setUnverified(err instanceof SignInError && err.code === 'unverified');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="md:hidden fixed top-0 left-0 right-0 h-[72px] flex items-center justify-center z-10 [background:linear-gradient(90deg,var(--nav-from)_0%,var(--nav-to)_100%)]">
        <img src="/logo.svg" alt="Logo" className="h-[40px] w-auto object-contain" />
      </div>

      <div className="flex-1 flex items-center justify-center bg-page-bg px-8 py-12 md:py-12 pt-[104px]">
        <div className="w-full max-w-[380px] flex flex-col gap-8">
          <div className="flex flex-col gap-1.5 text-center md:text-left">
            <h1 className="text-[28px] font-medium leading-tight text-page-title">Sign in</h1>
            <p className="text-sm text-page-muted" data-testid="login-subtitle">
              {appName ? `to continue to ${appName}` : 'Enter your credentials to continue'}
            </p>
          </div>

          <div className="bg-card-bg rounded-[8px] px-7 py-7 shadow-[0_4px_24px_0_var(--shadow-blue)] flex flex-col gap-5">
            {shownError && (
              <div
                role="alert"
                className="px-4 py-3 rounded-[5px] text-sm bg-[var(--error-bg)] text-error-text"
                data-testid="login-error"
              >
                {shownError}
                {unverified && (
                  <button
                    type="button"
                    data-testid="login-resend"
                    className="block mt-2 underline cursor-pointer bg-transparent border-0 p-0 text-sm text-error-text"
                    onClick={() =>
                      void resendSignup(email.trim())
                        .then(() => setResent(true))
                        .catch((err) =>
                          setError(err instanceof AccountsError ? err.message : 'The request failed.'),
                        )
                    }
                  >
                    {resent ? 'We sent the link again' : 'Send the link again'}
                  </button>
                )}
              </div>
            )}
            {showPassword && (
              <form onSubmit={onSubmit} className="flex flex-col gap-5" data-testid="login-form">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="email" className="text-sm font-medium text-page-text">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => void onEmailBlur()}
                    placeholder="you@company.com"
                    required
                    className={INPUT}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-baseline justify-between gap-2">
                    <label htmlFor="password" className="text-sm font-medium text-page-text">
                      Password
                    </label>
                    {/* Self-service reset by email link (ADR 0014 §10): only where everyone signs in with a password. */}
                    {options.password === 'on' && (
                      <Link
                        to={`/forgot-password${email.trim() ? `?email=${encodeURIComponent(email.trim())}` : ''}`}
                        data-testid="login-forgot"
                        className="text-xs text-nav-active hover:underline"
                      >
                        Forgot password?
                      </Link>
                    )}
                  </div>
                  <input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    className={INPUT}
                  />
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="login-submit"
                  className="h-9 rounded-[5px] text-sm font-medium text-white cursor-pointer border-0 disabled:opacity-50 disabled:cursor-not-allowed mt-1 bg-nav-active hover:bg-nav-active-dark transition-colors"
                >
                  {busy ? 'Signing in…' : 'Sign in'}
                </button>
              </form>
            )}

            {/* Single sign-on: one icon button per connection; none configured, no row. */}
            {buttons.length > 0 && (
              <div className="flex flex-col gap-4" data-testid="sso-buttons">
                {showPassword && (
                  <div className="flex items-center gap-3" aria-hidden="true">
                    <span className="h-px flex-1 bg-[var(--input-border)]" />
                    <span className="text-xs text-page-muted">or continue with</span>
                    <span className="h-px flex-1 bg-[var(--input-border)]" />
                  </div>
                )}
                <div
                  role="group"
                  aria-label="Single sign-on"
                  className="flex flex-wrap items-center justify-center gap-3"
                >
                  {buttons.map((c) => (
                    <button
                      key={c.id || 'installation'}
                      type="button"
                      id={c.id ? `sso-button-${c.id}` : 'sso-button'}
                      data-testid="sso-button"
                      data-connection={c.id}
                      data-provider={c.provider}
                      aria-label={`Sign in with ${c.label}`}
                      title={`Sign in with ${c.label}`}
                      onClick={() => startSso(c, comeBackUrl())}
                      disabled={busy}
                      className={ICON_BTN}
                    >
                      <ProviderIcon provider={c.provider} />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {showPassword && canSignUp && (
              <Link
                to="/signup"
                data-testid="login-signup"
                className="text-sm text-nav-active hover:underline"
              >
                Create an account
              </Link>
            )}

            {!showPassword && buttons.length === 0 && (
              <p className="text-sm text-page-muted m-0" data-testid="login-unavailable">
                No sign-in method is configured. Ask your administrator.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="hidden md:flex flex-col justify-between w-[45%] px-[60px] py-[50px] nav-sidebar-bg">
        <div className="w-[260px]">
          <img src="/logo.svg" alt="Logo" className="w-full h-auto object-contain" />
        </div>
        <div className="flex flex-col gap-5">
          <h2 className="text-[36px] font-medium leading-tight text-white">
            Business automation,
            <br />
            done right.
          </h2>
          <p className="text-sm leading-relaxed text-nav-text-dim">
            One platform for governed data, business workflows, AI agents, and the apps that run on them —
            from raw sources to deployed automation.
          </p>
        </div>
        <p className="text-xs text-nav-text-faint">TIVA Cortex — Business Automation Platform</p>
      </div>

      <span className="fixed bottom-2 right-3 text-[11px] text-page-muted opacity-50 select-none pointer-events-none">
        v{config.VITE_CORTEX_VERSION}
      </span>
    </div>
  );
}
